import {
  CloudUploadOutlined,
  DeleteOutline,
  Star,
  StarBorder,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { observer } from "mobx-react";
import { useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";
import { SwayyApiError } from "../../../api/SwayyProductsAPI";
import {
  SwayyProductImage,
  SwayyProductImageType,
} from "../../../models/images/SwayyProductImage";
import { SwayyProductImagesStore } from "../../../stores/SwayyProductImagesStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

interface SwayyProductImagesPanelProps {
  productId: number;
  store: SwayyProductImagesStore;
  onApiError: (error: SwayyApiError, title: string) => void;
  onSuccess: (message: string) => void;
  disabled?: boolean;
}

const IMAGE_TYPE_OPTIONS: { id: SwayyProductImageType; value: string }[] = [
  { id: "square", value: "Square" },
  { id: "landscape", value: "Landscape" },
  { id: "portrait", value: "Portrait" },
];

const CARD_WIDTH = 132;
const IMAGE_HEIGHT = 96;
const CARD_HEIGHT = IMAGE_HEIGHT + 8;

const primaryBtnSx = {
  minHeight: 34,
  px: 1.75,
  fontSize: 13,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
} as const;

const ScopeChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <Chip
    label={label}
    clickable
    onClick={onClick}
    sx={{
      fontFamily: swayyFont,
      fontWeight: 600,
      height: 30,
      backgroundColor: active ? swayyColors.pineLight : swayyColors.pageBg,
      color: active ? swayyColors.deepGreen : swayyColors.textSecondary,
      border: `1px solid ${active ? swayyColors.pine : swayyColors.border}`,
      "&:hover": {
        backgroundColor: swayyColors.pineLight,
      },
    }}
  />
);

const UploadTypePicker = ({
  value,
  disabled,
  onChange,
}: {
  value: SwayyProductImageType;
  disabled: boolean;
  onChange: (next: SwayyProductImageType) => void;
}) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    flexWrap="wrap"
    useFlexGap
  >
    <CustomTypography
      variant="caption"
      color={swayyColors.textSecondary}
      sx={{ fontWeight: 600 }}
    >
      Image type
    </CustomTypography>
    {IMAGE_TYPE_OPTIONS.map((option) => {
      const active = option.id === value;
      return (
        <Chip
          key={option.id}
          label={option.value}
          clickable={!disabled}
          onClick={() => {
            if (!disabled) onChange(option.id);
          }}
          sx={{
            fontFamily: swayyFont,
            fontWeight: 600,
            height: 26,
            backgroundColor: active
              ? swayyColors.pineLight
              : swayyColors.pageBg,
            color: active ? swayyColors.deepGreen : swayyColors.textSecondary,
            border: `1px solid ${active ? swayyColors.pine : swayyColors.border}`,
            opacity: disabled ? 0.55 : 1,
            "&:hover": {
              backgroundColor: swayyColors.pineLight,
            },
          }}
        />
      );
    })}
  </Stack>
);

const variantChipLabel = (sku: string, name: string, id: number) =>
  sku || name || `Variant #${id}`;

const VariantSkuChip = ({
  sku,
  name,
  id,
  count,
  active,
  disabled,
  onClick,
}: {
  sku: string;
  name: string;
  id: number;
  count: number;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) => (
  <Chip
    clickable={!disabled}
    onClick={() => {
      if (!disabled) onClick();
    }}
    label={
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
      >
        {variantChipLabel(sku, name, id)}
        <Box
          component="span"
          sx={{
            ml: 0.25,
            px: 0.75,
            minWidth: 18,
            height: 18,
            borderRadius: swayyRadii.full,
            fontSize: 11,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: active
              ? alpha(swayyColors.white, 0.35)
              : alpha(swayyColors.pine, 0.08),
            color: active ? swayyColors.deepGreen : swayyColors.textSecondary,
          }}
        >
          {count}
        </Box>
      </Box>
    }
    sx={{
      fontFamily: swayyFont,
      fontWeight: 600,
      height: "auto",
      py: 0.5,
      backgroundColor: active ? swayyColors.pineLight : swayyColors.pageBg,
      color: active ? swayyColors.deepGreen : swayyColors.textSecondary,
      border: `1px solid ${active ? swayyColors.pine : swayyColors.border}`,
      opacity: disabled ? 0.55 : 1,
      "&:hover": {
        backgroundColor: swayyColors.pineLight,
      },
      "& .MuiChip-label": {
        whiteSpace: "normal",
        display: "block",
      },
    }}
  />
);

const UploadDropzone = ({
  compact,
  busy,
  uploading,
  onClick,
}: {
  compact?: boolean;
  busy: boolean;
  uploading: boolean;
  onClick: () => void;
}) => (
  <Box
    onClick={() => {
      if (!busy) onClick();
    }}
    sx={{
      width: compact ? CARD_WIDTH : "100%",
      height: compact ? CARD_HEIGHT : 140,
      border: `1px dashed ${swayyColors.border}`,
      borderRadius: swayyRadii.md,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      px: 1.5,
      textAlign: "center",
      cursor: busy ? "default" : "pointer",
      backgroundColor: alpha(swayyColors.pine, 0.02),
      "&:hover": busy
        ? undefined
        : {
            borderColor: swayyColors.pine,
            backgroundColor: alpha(swayyColors.pine, 0.06),
          },
    }}
  >
    {uploading ? (
      <CircularProgress size={22} sx={{ color: swayyColors.pine }} />
    ) : (
      <CloudUploadOutlined
        sx={{
          fontSize: compact ? 22 : 28,
          color: swayyColors.textSecondary,
        }}
      />
    )}
    <CustomTypography
      variant="body2"
      color={swayyColors.deepGreen}
      sx={{ fontWeight: 600, fontSize: 12 }}
    >
      {uploading ? "Uploading…" : compact ? "Add more" : "Click to upload"}
    </CustomTypography>
    {!compact && !uploading && (
      <CustomTypography variant="body2" color={swayyColors.textSecondary}>
        JPG, PNG, or WebP
      </CustomTypography>
    )}
  </Box>
);

const ImageCard = ({
  image,
  index,
  busy,
  isDragging,
  isDropTarget,
  onSetDefault,
  onDelete,
  onPreview,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  image: SwayyProductImage;
  index: number;
  busy: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  onSetDefault: () => void;
  onDelete: () => void;
  onPreview: () => void;
  onDragStart: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      draggable={!busy}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      sx={{
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        p: 0.5,
        display: "flex",
        flexDirection: "column",
        borderRadius: swayyRadii.md,
        position: "relative",
        borderColor: isDropTarget ? swayyColors.pine : swayyColors.border,
        opacity: isDragging ? 0.45 : 1,
        cursor: busy ? "default" : "grab",
        "&:active": { cursor: busy ? "default" : "grabbing" },
      }}
    >
      <Box
        sx={{ position: "relative", flex: 1, minHeight: 0 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Box
          component="img"
          src={image.url}
          alt={image.alt || `Image ${image.id}`}
          draggable={false}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 1,
            display: "block",
            bgcolor: swayyColors.pageBg,
          }}
        />
        {hovered && image.url ? (
          <Box
            onClick={(event) => {
              event.stopPropagation();
              if (!busy) onPreview();
            }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            aria-label={`View image ${index + 1}`}
          >
            <VisibilityOutlined
              sx={{ color: swayyColors.white, fontSize: 20 }}
            />
          </Box>
        ) : null}
      </Box>

      {image.isDefault && (
        <Chip
          size="small"
          icon={<Star sx={{ fontSize: "12px !important" }} />}
          label="Default"
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            height: 20,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: swayyColors.white,
            color: swayyColors.deepGreen,
            zIndex: 1,
            "& .MuiChip-icon": { ml: 0.4 },
          }}
        />
      )}

      <Stack
        direction="row"
        spacing={0.25}
        sx={{ position: "absolute", top: 6, right: 6, zIndex: 1 }}
      >
        <Tooltip title={image.isDefault ? "Default image" : "Set as default"}>
          <span>
            <IconButton
              size="small"
              disabled={busy || image.isDefault}
              onClick={(event) => {
                event.stopPropagation();
                onSetDefault();
              }}
              sx={{
                bgcolor: swayyColors.white,
                border: `1px solid ${swayyColors.border}`,
                color: swayyColors.pine,
                width: 24,
                height: 24,
                "&:hover": { bgcolor: swayyColors.white },
              }}
            >
              {image.isDefault ? (
                <Star sx={{ fontSize: 14 }} />
              ) : (
                <StarBorder sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove image">
          <span>
            <IconButton
              size="small"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              sx={{
                bgcolor: swayyColors.white,
                border: `1px solid ${swayyColors.border}`,
                color: swayyColors.danger,
                width: 24,
                height: 24,
                "&:hover": { bgcolor: swayyColors.white },
              }}
              aria-label="Remove image"
            >
              <DeleteOutline sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

const SwayyProductImagesPanel = ({
  productId,
  store,
  onApiError,
  onSuccess,
  disabled = false,
}: SwayyProductImagesPanelProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const busy = disabled || store.isUploading || store.isUpdating;
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<
    { id?: number; src: string; alt?: string }[]
  >([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryTitle, setGalleryTitle] = useState("Images");

  const handleUploadClick = () => {
    if (disabled) return;
    if (!store.canUpload) {
      onApiError(
        new SwayyApiError("Select a variant before uploading images."),
        "Select a variant before uploading images.",
      );
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (disabled) return;
    try {
      const files = Array.from(event.target.files ?? []);
      event.target.value = "";
      if (!files.length) return;
      const error = await store.uploadAndCreate(productId, files);
      if (error) {
        onApiError(error, "Failed to upload images.");
      }
    } catch (error) {
      onApiError(
        error instanceof SwayyApiError
          ? error
          : new SwayyApiError("Unable to upload images."),
        "Failed to upload images.",
      );
    }
  };

  const openGallery = (startIndex: number) => {
    try {
      setGalleryImages(
        store.scopedImages.map((image, index) => ({
          id: image.id,
          src: image.url,
          alt: image.alt || `Image ${index + 1}`,
        })),
      );
      setGalleryIndex(startIndex);
      setGalleryTitle(
        store.scope === "variant" && store.selectedVariant
          ? variantChipLabel(
              store.selectedVariant.sku,
              store.selectedVariant.name,
              store.selectedVariant.id,
            )
          : "Product images",
      );
      setGalleryOpen(true);
    } catch {
      onApiError(new SwayyApiError("Failed to open image preview."), "Failed to open image preview.");
    }
  };

  const handleReorder = async (fromId: number, toId: number) => {
    try {
      if (fromId === toId) return;
      const scoped = [...store.scopedImages];
      const fromIndex = scoped.findIndex((image) => image.id === fromId);
      const toIndex = scoped.findIndex((image) => image.id === toId);
      if (fromIndex < 0 || toIndex < 0) return;
      const reordered = [...scoped];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      const error = await store.reorderImages(
        productId,
        reordered.map((image) => image.id),
      );
      if (error) onApiError(error, "Failed to reorder images.");
    } catch (error) {
      onApiError(
        error instanceof SwayyApiError
          ? error
          : new SwayyApiError("Unable to reorder images."),
        "Failed to reorder images.",
      );
    }
  };

  if (store.loadState === "loading" || store.loadState === "idle") {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={40} width={280} />
          <Box display="flex" flexWrap="wrap" gap={1.5}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width={CARD_WIDTH}
                height={128}
              />
            ))}
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (store.loadState === "error") {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          textAlign: "center",
        }}
      >
        <CustomTypography
          variant="body1"
          color={swayyColors.danger}
          sx={{ mb: 2 }}
        >
          {store.errorMessage || "Unable to load images."}
        </CustomTypography>
        <AppButton
          label="Retry"
          variant="primary"
          onClick={() => {
            void store.loadImagesTab(productId).then((error) => {
              if (error) onApiError(error, "Failed to load images.");
            });
          }}
          customStyles={primaryBtnSx}
        />
      </Paper>
    );
  }

  const scoped = store.scopedImages;
  const showVariantPicker = store.scope === "variant";
  const hasVariants = store.variants.length > 0;
  const showVariantGallery = showVariantPicker && hasVariants;
  const showProductEmpty = store.scope === "product" && scoped.length === 0;
  const showVariantEmpty = showVariantGallery && scoped.length === 0;

  const imageCards = scoped.map((image, index) => (
    <ImageCard
      key={image.id}
      image={image}
      index={index}
      busy={busy}
      isDragging={dragId === image.id}
      isDropTarget={dropTargetId === image.id && dragId !== image.id}
      onSetDefault={() => {
        void store.setDefault(image.id).then((error) => {
          if (error) onApiError(error, "Failed to set default image.");
          else onSuccess("Default image updated.");
        });
      }}
      onDelete={() => {
        void store.deleteImage(productId, image.id).then((error) => {
          if (error) onApiError(error, "Failed to delete image.");
        });
      }}
      onPreview={() => openGallery(index)}
      onDragStart={(event) => {
        if (busy) {
          event.preventDefault();
          return;
        }
        setDragId(image.id);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(image.id));
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        if (dropTargetId !== image.id) setDropTargetId(image.id);
      }}
      onDrop={(event) => {
        event.preventDefault();
        const fromId = Number(event.dataTransfer.getData("text/plain"));
        setDropTargetId(null);
        setDragId(null);
        if (!Number.isInteger(fromId) || fromId <= 0) return;
        void handleReorder(fromId, image.id);
      }}
      onDragEnd={() => {
        setDragId(null);
        setDropTargetId(null);
      }}
    />
  ));

  const uploadTypePicker = (
    <UploadTypePicker
      value={store.uploadImageType}
      disabled={busy}
      onChange={(next) => store.setUploadImageType(next)}
    />
  );

  const galleryRow = (
    <Stack spacing={1.5}>
      {uploadTypePicker}
      <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={1.5}>
        {imageCards}
        <UploadDropzone
          compact
          busy={busy}
          uploading={store.isUploading}
          onClick={handleUploadClick}
        />
      </Box>
    </Stack>
  );

  const emptyUpload = (
    <Stack spacing={1.5}>
      {uploadTypePicker}
      <UploadDropzone
        busy={busy}
        uploading={store.isUploading}
        onClick={handleUploadClick}
      />
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          backgroundColor: swayyColors.white,
        }}
      >
        <Stack spacing={1}>
          <CustomTypography
            variant="heading5"
            color={swayyColors.deepGreen}
            sx={{ fontWeight: 700 }}
          >
            Images
          </CustomTypography>
          <CustomTypography variant="body2" color={swayyColors.textSecondary}>
            Product default is used for listing. Variant images override when
            that variant is selected. Drag tiles to reorder.
          </CustomTypography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ScopeChip
              label="Product images"
              active={store.scope === "product"}
              onClick={() => store.setScope("product")}
            />
            <ScopeChip
              label="Variant images"
              active={store.scope === "variant"}
              onClick={() => store.setScope("variant")}
            />
          </Stack>
        </Stack>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(event) => void handleFilesSelected(event)}
        />
      </Paper>

      {showVariantPicker && !hasVariants && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: swayyRadii.lg,
            borderColor: swayyColors.border,
            backgroundColor: alpha(swayyColors.pine, 0.04),
          }}
        >
          <CustomTypography variant="body2" color={swayyColors.textSecondary}>
            Create variants first, then attach images to each variant.
          </CustomTypography>
        </Paper>
      )}

      {showVariantGallery && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: swayyRadii.lg,
            borderColor: swayyColors.border,
            backgroundColor: swayyColors.white,
          }}
        >
          <CustomTypography
            variant="body2"
            color={swayyColors.textSecondary}
            sx={{ mb: 1.5 }}
          >
            Click a variant, then upload or manage images for that SKU.
          </CustomTypography>
          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {store.variants.map((variant) => (
              <VariantSkuChip
                key={variant.id}
                id={variant.id}
                sku={variant.sku}
                name={variant.name}
                count={store.imageCountFor(variant.id)}
                active={variant.id === store.selectedVariantId}
                disabled={busy}
                onClick={() => store.setSelectedVariantId(variant.id)}
              />
            ))}
          </Box>
          {store.selectedVariant && (
            <CustomTypography
              variant="body2"
              color={swayyColors.deepGreen}
              sx={{ fontWeight: 600, mb: 1.5 }}
            >
              {variantChipLabel(
                store.selectedVariant.sku,
                store.selectedVariant.name,
                store.selectedVariant.id,
              )}
            </CustomTypography>
          )}
          {showVariantEmpty ? emptyUpload : galleryRow}
        </Paper>
      )}

      {store.scope === "product" && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: swayyRadii.lg,
            borderColor: swayyColors.border,
            backgroundColor: swayyColors.white,
          }}
        >
          {showProductEmpty ? emptyUpload : galleryRow}
        </Paper>
      )}

      <ImageGalleryPopup
        open={galleryOpen}
        images={galleryImages}
        title={galleryTitle}
        initialIndex={galleryIndex}
        onClose={() => setGalleryOpen(false)}
      />
    </Stack>
  );
};

export default observer(SwayyProductImagesPanel);
